import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { TopNav } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Copy, Shuffle, Users, Mail, Share2, MessageCircle, EyeOff, Timer as TimerIcon } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { warn } from "@/lib/logger";

export default function RoomLobby() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/rooms/${id}/state`);
      setRoom(data);
      if (data.phase === "live") nav(`/rooms/${id}/live`);
    } catch (err) {
      warn("lobby state load failed:", err);
      toast.error("Errore caricamento sala");
    }
  }, [id, nav]);

  useEffect(() => {
    load();
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [load]);

  if (!room) return <div><TopNav /><div className="p-10 text-center text-slate-400">Caricamento…</div></div>;

  const isCreator = user?.id === room.creator_id;
  const joined = room.teams.filter((t) => t.user_id);
  const empty = room.teams.filter((t) => !t.user_id);

  const startDraw = async () => {
    setLoading(true);
    try {
      await api.post(`/rooms/${id}/start-draw`);
      toast.success("Sorteggio effettuato — l'asta è partita!");
      nav(`/rooms/${id}/live`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(room.code);
    toast.success("Codice copiato!");
  };

  const inviteUrl = `${window.location.origin}/join/${room.code}`;
  const inviteText = `Ti invito alla nostra asta del Fantacalcio "${room.name}" su AstaFantaLive! Entra da qui: ${inviteUrl}`;

  const copyLink = () => {
    navigator.clipboard.writeText(inviteUrl);
    toast.success("Link invito copiato!");
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: room.name, text: inviteText, url: inviteUrl });
      } catch (err) {
        // Native share cancelled by user — not an error worth surfacing.
        if (err && err.name !== "AbortError") warn("Share failed:", err);
      }
    } else {
      copyLink();
    }
  };

  const shareWhatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(inviteText)}`, "_blank");
  const shareEmail = () => window.location.href = `mailto:?subject=${encodeURIComponent("Invito AstaFantaLive: " + room.name)}&body=${encodeURIComponent(inviteText)}`;

  return (
    <div>
      <TopNav />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="label-eyebrow mb-2">Lobby di attesa</div>
            <h1 className="text-4xl font-black tracking-tight">{room.name}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <span className={`text-[10px] uppercase tracking-widest font-black px-2 py-1 rounded ${room.auction_mode === "secret" ? "bg-amber-500/20 text-amber-400" : "bg-emerald-500/20 text-emerald-400"}`}>
                {room.auction_mode === "secret" ? <><EyeOff className="w-3 h-3 inline mr-1" />Segreta</> : "Classica"}
              </span>
              <span className="text-[10px] uppercase tracking-widest font-black px-2 py-1 rounded bg-slate-800 text-slate-300">
                <TimerIcon className="w-3 h-3 inline mr-1" />{room.timer_seconds}s
              </span>
              <span className="text-xs text-slate-400">• {room.initial_credits} FM</span>
            </div>
          </div>
          <div className="glass rounded-2xl p-4 flex items-center gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Codice invito</div>
              <div className="font-mono text-3xl font-black text-emerald-400" data-testid="room-code">{room.code}</div>
            </div>
            <Button data-testid="copy-code-btn" variant="outline" size="sm" onClick={copyCode} className="border-emerald-500/30">
              <Copy className="w-4 h-4 mr-1" />Copia
            </Button>
          </div>
        </div>

        {/* Invite / QR share panel */}
        <div className="glass rounded-2xl p-6 mb-6 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center" data-testid="invite-panel">
          <div className="bg-white p-3 rounded-xl w-fit mx-auto md:mx-0">
            <QRCodeSVG value={inviteUrl} size={168} bgColor="#ffffff" fgColor="#0f172a" level="M" />
          </div>
          <div>
            <div className="label-eyebrow mb-2">Invita i partecipanti</div>
            <h3 className="text-xl font-black mb-2">Un click, una scansione, e sono dentro</h3>
            <p className="text-sm text-slate-400 mb-4">Condividi il link o il QR. I fantallenatori dovranno solo inserire il nome della loro squadra.</p>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 font-mono text-xs bg-slate-900/70 border border-white/10 rounded-lg px-3 py-2 truncate" data-testid="invite-url">{inviteUrl}</div>
              <Button data-testid="copy-link-btn" variant="outline" size="sm" onClick={copyLink} className="border-emerald-500/30">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="share-whatsapp-btn" onClick={shareWhatsapp} className="bg-[#25D366] hover:bg-[#22c15b] text-white font-bold h-10">
                <MessageCircle className="w-4 h-4 mr-2" />WhatsApp
              </Button>
              <Button data-testid="share-email-btn" onClick={shareEmail} variant="outline" className="border-emerald-500/30 hover:bg-emerald-500/10 h-10">
                <Mail className="w-4 h-4 mr-2" />Email
              </Button>
              <Button data-testid="share-native-btn" onClick={shareNative} variant="outline" className="border-emerald-500/30 hover:bg-emerald-500/10 h-10">
                <Share2 className="w-4 h-4 mr-2" />Condividi…
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <div className="label-eyebrow">Presenti ({joined.length}/{room.teams.length})</div>
            </div>
            <div className="space-y-2" data-testid="joined-list">
              {joined.map((t) => (
                <div key={t.id} className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                  <div>
                    <div className="font-bold text-white">{t.name}</div>
                    <div className="text-xs text-slate-400">{t.user_name}{t.user_id === room.creator_id && " • Creatore"}</div>
                  </div>
                  <div className="text-emerald-400 font-mono font-bold">{t.credits} FM</div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-slate-500" />
              <div className="label-eyebrow" style={{ color: "#94a3b8" }}>Slot liberi ({empty.length})</div>
            </div>
            <div className="space-y-2" data-testid="empty-list">
              {empty.length === 0 && <div className="text-sm text-slate-500 italic">Tutti i posti sono stati occupati.</div>}
              {empty.map((t) => (
                <div key={t.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/40 border border-white/5">
                  <div>
                    <div className="font-bold text-slate-300">{t.name}</div>
                    <div className="text-xs text-slate-500">In attesa…</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-white/5 text-xs text-slate-400">
              Condividi anche il codice <span className="font-mono text-emerald-400">{room.code}</span> come alternativa al link/QR.
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          {isCreator ? (
            <Button data-testid="start-draw-btn" onClick={startDraw} disabled={loading || joined.length < 2} className="h-12 px-8 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold">
              <Shuffle className="w-5 h-5 mr-2" />{loading ? "Sorteggio…" : "Sorteggia ordine e avvia l'asta"}
            </Button>
          ) : (
            <div className="text-sm text-slate-400 italic">In attesa che il creatore avvii il sorteggio…</div>
          )}
        </div>
      </div>
    </div>
  );
}
